import { FC } from 'react';

import { IEditorRule, IEditorRuleInput } from '@entities/routing/config-version';
import ModalConfirm from '@features/modal-confirm';
import ModalOpenBtn from '@features/modal-open-btn';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useModal } from '@shared/components/Modal';

import ChangeMarker from './ChangeMarker';
import { DraggableRow, DropZoneRow } from './DraggableRow';
import RuleEditCells from './RuleEditCells';
import RuleEditorForm from './RuleEditorForm';
import RuleParams from './RuleParams';

interface IProps {
  // In list order — the mapper sorts by priority on load.
  rules: IEditorRule[];
  isEditing?: boolean;
  // Scopes the drag type: rules only move within their policy.
  policyUid?: string;
  // Rules that differ from the saved version, marked on their rows.
  changedRuleUids?: string[];
  // Subset of changedRuleUids: rules created in this session.
  addedRuleUids?: string[];
  onMoveRule?: (policyUid: string, ruleUid: string, toIndex: number) => void;
  // Rule edits — working copy only, no requests.
  onAddRule?: (policyUid: string, input: IEditorRuleInput) => void;
  onUpdateRule?: (policyUid: string, ruleUid: string, input: IEditorRuleInput) => void;
  onRemoveRule?: (policyUid: string, ruleUid: string) => void;
  onToggleRule?: (policyUid: string, ruleUid: string) => void;
}

// On/Off is shown in both modes — locked outside edit mode, like the policy switch.
const READ_COLUMNS = [
  'Priority',
  'Name',
  'Conditions',
  'Selection',
  'Limits',
  'Strategy',
  'On/Off',
];
// Edit mode adds the row actions.
const EDIT_COLUMNS = ['Actions'];
const CENTERED_COLUMNS = ['On/Off', 'Actions'];

// Twin of PolicyRulesTable (settings/policies): its drag-to-reorder, New rule and row
// actions in edit mode; read-only otherwise (the On/Off switch stays visible but locked).
// No Overrides column (no backend).
const RulesTable: FC<IProps> = ({
  rules,
  isEditing = false,
  policyUid = '',
  changedRuleUids = [],
  addedRuleUids = [],
  onMoveRule,
  onAddRule,
  onUpdateRule,
  onRemoveRule,
  onToggleRule,
}) => {
  const { openModal, closeModal } = useModal();
  const dragType = `routing-rule-row:${policyUid}`;
  const columns = isEditing ? [...READ_COLUMNS, ...EDIT_COLUMNS] : READ_COLUMNS;
  const onMove = (ruleUid: string, toIndex: number) => onMoveRule?.(policyUid, ruleUid, toIndex);

  const openEdit = (rule: IEditorRule) =>
    openModal(
      <RuleEditorForm rule={rule} onSubmit={input => onUpdateRule?.(policyUid, rule.uid, input)} />,
      'md',
    );

  const openDelete = (rule: IEditorRule) =>
    openModal(
      <ModalConfirm
        title='Delete rule'
        message={`Do you want to delete rule "${rule.name}"?`}
        onConfirm={() => {
          onRemoveRule?.(policyUid, rule.uid);
          closeModal();
        }}
        onCancel={closeModal}
      />,
      'sm',
    );

  const renderRow = (rule: IEditorRule, index: number) => {
    const isChanged = changedRuleUids.includes(rule.uid);

    return (
      <TableRow
        key={rule.uid}
        className={`routing-rule-row${isChanged ? ' routing-rule-row--changed' : ''}`}
      >
        <TableCell align='left' sx={{ width: 120 }}>
          <Stack direction='row' spacing={0.5} alignItems='center'>
            {isEditing && (
              <DragIndicatorIcon
                sx={{ color: 'rgba(0, 0, 0, 0.5)' }}
                fontSize='small'
                className='drag-handle'
              />
            )}
            {/* Position = the priority Save Draft sends */}
            <Chip size='small' variant='outlined' label={`${index + 1}`} />
          </Stack>
        </TableCell>
        <TableCell align='left' sx={{ width: 120 }}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Typography variant='body2' fontWeight={600}>
              {rule.name}
            </Typography>
            {isEditing && (
              <ChangeMarker isAdded={addedRuleUids.includes(rule.uid)} isChanged={isChanged} />
            )}
          </Stack>
          {rule.description && (
            <Typography variant='caption' color='text.secondary'>
              {rule.description}
            </Typography>
          )}
        </TableCell>
        <TableCell align='left' sx={{ minWidth: 180 }}>
          <RuleParams params={rule.conditions} />
        </TableCell>
        <TableCell align='left'>
          <RuleParams params={rule.selections} />
        </TableCell>
        <TableCell align='left'>
          <Typography variant='body2' color='text.primary'>
            {rule.min ?? 0} - {rule.max ?? 0}
          </Typography>
        </TableCell>
        <TableCell align='left'>
          <Typography variant='body2' color='primary'>
            {(rule.sort_strategy || 'random').toUpperCase()}
          </Typography>
        </TableCell>
        <RuleEditCells
          rule={rule}
          isEditing={isEditing}
          onToggle={() => onToggleRule?.(policyUid, rule.uid)}
          onEdit={() => openEdit(rule)}
          onDelete={() => openDelete(rule)}
        />
      </TableRow>
    );
  };

  return (
    <Box sx={{ my: 1 }}>
      <div className='routing-config-rules-header'>
        <Typography variant='body1' fontWeight={700}>
          Rules (Limits)
        </Typography>
        {isEditing && (
          <ModalOpenBtn
            text='New rule'
            size='md'
            modalContent={<RuleEditorForm onSubmit={input => onAddRule?.(policyUid, input)} />}
          />
        )}
      </div>

      {!rules.length ? (
        <Typography variant='body2' color='text.secondary'>
          No rules in this policy.
        </Typography>
      ) : (
        <Table size='small' className='routing-config-rules-table'>
          <TableHead>
            <TableRow>
              {columns.map(title => (
                <TableCell
                  key={title}
                  align={CENTERED_COLUMNS.includes(title) ? 'center' : 'left'}
                  width={title === 'Limits' ? '100px' : undefined}
                >
                  {title}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isEditing
              ? rules.map((rule, index) => (
                  <DraggableRow
                    key={rule.uid}
                    type={dragType}
                    id={rule.uid}
                    index={index}
                    row={renderRow(rule, index)}
                    onMove={onMove}
                  />
                ))
              : rules.map((rule, index) => renderRow(rule, index))}
            {isEditing && (
              <DropZoneRow
                type={dragType}
                count={rules.length}
                row={
                  <TableRow>
                    <TableCell colSpan={columns.length} />
                  </TableRow>
                }
                onMove={onMove}
              />
            )}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

export default RulesTable;
